import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '@/lib/auth-context';
import { complianceAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Calendar, CalendarDays, ChevronLeft, ChevronRight, AlertTriangle, Clock, CheckCircle2, Bell, Send } from 'lucide-react';
import { toast } from 'sonner';
import logger from '@/lib/logger';

const STAGE_LABELS = {
  not_started: 'Not Started',
  documents_pending: 'Docs Pending',
  documents_received: 'Docs Received',
  ready_for_review: 'Ready for Review',
  tax_payable: 'Tax Payable',
  ready_to_file: 'Ready to File',
  filed: 'Filed',
  paid: 'Paid',
};

const STAGE_COLORS = {
  not_started: 'bg-gray-100 text-gray-700',
  documents_pending: 'bg-amber-100 text-amber-700',
  documents_received: 'bg-blue-100 text-blue-700',
  ready_for_review: 'bg-purple-100 text-purple-700',
  tax_payable: 'bg-rose-100 text-rose-700',
  ready_to_file: 'bg-indigo-100 text-indigo-700',
  filed: 'bg-green-100 text-green-700',
  paid: 'bg-green-200 text-green-800',
};

const URGENCY_CONFIG = {
  overdue: { label: 'Overdue', color: 'bg-red-100 text-red-700 border-red-200', icon: AlertTriangle, dotColor: 'bg-red-500' },
  due_today: { label: 'Due Today', color: 'bg-orange-100 text-orange-700 border-orange-200', icon: Clock, dotColor: 'bg-orange-500' },
  due_this_week: { label: 'Due This Week', color: 'bg-amber-100 text-amber-700 border-amber-200', icon: Clock, dotColor: 'bg-amber-500' },
  upcoming: { label: 'Upcoming', color: 'bg-blue-100 text-blue-700 border-blue-200', icon: CalendarDays, dotColor: 'bg-blue-500' },
};

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const getCalDayCellClass = (isSelected, isToday, hasEvents) => {
  if (isSelected) return 'border-primary bg-primary/5 ring-2 ring-primary/20';
  if (isToday) return 'border-primary/40 bg-primary/5';
  if (hasEvents) return 'border-border hover:border-primary/30 hover:bg-muted/50';
  return 'border-transparent hover:bg-muted/30';
};

const getEventDotColor = (ev, today) => {
  if (ev.stage === 'filed' || ev.stage === 'paid') return 'bg-green-500';
  if (new Date(ev.compliance_due_date) < today) return 'bg-red-500';
  return 'bg-blue-500';
};

function getDaysInMonth(year, month) {
  return new Date(year, month + 1, 0).getDate();
}

function getFirstDayOfMonth(year, month) {
  return new Date(year, month, 1).getDay();
}

function formatDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

function isSameDay(d1, d2) {
  return d1.getFullYear() === d2.getFullYear() && d1.getMonth() === d2.getMonth() && d1.getDate() === d2.getDate();
}

const ComplianceCalendarPage = () => {
  const { user } = useAuth();
  const [calendarData, setCalendarData] = useState({ tasks: [], subtasks: [] });
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [selectedDate, setSelectedDate] = useState(null);
  const [filterUrgency, setFilterUrgency] = useState('all');
  const [sendingReminder, setSendingReminder] = useState(null);

  const canManage = user?.role === 'admin' || user?.role === 'staff';

  const loadData = useCallback(async () => {
    try {
      const [calRes, remRes] = await Promise.all([
        complianceAPI.calendar(),
        complianceAPI.reminders()
      ]);
      setCalendarData(calRes.data);
      setReminders(remRes.data);
    } catch (err) {
      logger.error('Failed to load compliance data', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleSendReminder = async (taskId) => {
    setSendingReminder(taskId);
    try {
      const res = await complianceAPI.sendReminder(taskId);
      toast.success(res.data.message || 'Reminder sent');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send reminder');
    } finally {
      setSendingReminder(null);
    }
  };

  // Build a map of dates to events for the calendar
  const dateEventsMap = useMemo(() => {
    const map = {};
    const allItems = [
      ...calendarData.tasks.map(t => ({ ...t, itemType: 'task' })),
      ...calendarData.subtasks.map(s => ({ ...s, itemType: 'subtask' })),
    ];
    allItems.forEach(item => {
      const due = item.compliance_due_date;
      if (!due) return;
      const dateKey = due.substring(0, 10); // YYYY-MM-DD
      if (!map[dateKey]) map[dateKey] = [];
      map[dateKey].push(item);
    });
    return map;
  }, [calendarData]);

  // Calendar grid
  const daysInMonth = getDaysInMonth(currentYear, currentMonth);
  const firstDay = getFirstDayOfMonth(currentYear, currentMonth);
  const today = new Date();
  const todayKey = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

  const calendarDays = [];
  // Empty cells before first day
  for (let i = 0; i < firstDay; i++) {
    calendarDays.push(null);
  }
  for (let d = 1; d <= daysInMonth; d++) {
    const dateKey = `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    calendarDays.push({ day: d, dateKey, events: dateEventsMap[dateKey] || [] });
  }

  const navigateMonth = (dir) => {
    let m = currentMonth + dir;
    let y = currentYear;
    if (m < 0) { m = 11; y--; }
    if (m > 11) { m = 0; y++; }
    setCurrentMonth(m);
    setCurrentYear(y);
    setSelectedDate(null);
  };

  const selectedEvents = selectedDate ? (dateEventsMap[selectedDate] || []) : [];

  // Filter reminders
  const filteredReminders = filterUrgency === 'all'
    ? reminders
    : reminders.filter(r => r.status === filterUrgency);

  // Summary counts
  const overdueCnt = reminders.filter(r => r.status === 'overdue').length;
  const todayCnt = reminders.filter(r => r.status === 'due_today').length;
  const weekCnt = reminders.filter(r => r.status === 'due_this_week').length;
  const upcomingCnt = reminders.filter(r => r.status === 'upcoming').length;

  if (loading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map(i => <div key={i} className="h-32 bg-muted rounded-xl animate-pulse" />)}
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="compliance-calendar-page">
      <div>
        <h2 className="text-xl sm:text-2xl font-semibold" style={{ fontFamily: 'Space Grotesk' }}>Compliance Calendar</h2>
        <p className="text-muted-foreground text-sm mt-1">Track all compliance deadlines and send reminders</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: 'Overdue', count: overdueCnt, color: 'text-red-600', bg: 'bg-red-50 border-red-200', icon: AlertTriangle },
          { label: 'Due Today', count: todayCnt, color: 'text-orange-600', bg: 'bg-orange-50 border-orange-200', icon: Clock },
          { label: 'Due This Week', count: weekCnt, color: 'text-amber-600', bg: 'bg-amber-50 border-amber-200', icon: Calendar },
          { label: 'Upcoming', count: upcomingCnt, color: 'text-blue-600', bg: 'bg-blue-50 border-blue-200', icon: CalendarDays },
        ].map(s => (
          <Card key={s.label} className={`border ${s.bg} transition-shadow duration-200 hover:shadow-[var(--shadow-md)]`} data-testid={`summary-${s.label.toLowerCase().replace(/\s+/g, '-')}`}>
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-3">
                <s.icon className={`h-5 w-5 ${s.color}`} />
              </div>
              <p className="text-2xl font-bold">{s.count}</p>
              <p className="text-xs text-muted-foreground mt-1">{s.label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <Tabs defaultValue="calendar" className="space-y-4">
        <TabsList data-testid="calendar-tabs">
          <TabsTrigger value="calendar" data-testid="tab-calendar">Calendar View</TabsTrigger>
          <TabsTrigger value="list" data-testid="tab-list">List View</TabsTrigger>
        </TabsList>

        {/* Calendar View */}
        <TabsContent value="calendar">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Calendar Grid */}
            <Card className="lg:col-span-2">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <Button variant="ghost" size="sm" onClick={() => navigateMonth(-1)} data-testid="cal-prev-month">
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <CardTitle className="text-lg" style={{ fontFamily: 'Space Grotesk' }}>
                    {MONTHS[currentMonth]} {currentYear}
                  </CardTitle>
                  <Button variant="ghost" size="sm" onClick={() => navigateMonth(1)} data-testid="cal-next-month">
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {/* Weekday headers */}
                <div className="grid grid-cols-7 gap-1 mb-2">
                  {WEEKDAYS.map(wd => (
                    <div key={wd} className="text-center text-xs font-medium text-muted-foreground py-2">{wd}</div>
                  ))}
                </div>
                {/* Day cells */}
                <div className="grid grid-cols-7 gap-1">
                  {calendarDays.map((cell, idx) => {
                    if (!cell) return <div key={`empty-${idx}`} className="h-20" />;
                    const isToday = cell.dateKey === todayKey;
                    const isSelected = cell.dateKey === selectedDate;
                    const hasEvents = cell.events.length > 0;
                    const hasOverdue = cell.events.some(e => {
                      const d = new Date(e.compliance_due_date);
                      return d < today && e.stage !== 'filed';
                    });

                    return (
                      <button
                        key={cell.dateKey}
                        onClick={() => setSelectedDate(cell.dateKey === selectedDate ? null : cell.dateKey)}
                        className={`h-20 p-1.5 rounded-lg border text-left transition-all duration-150 flex flex-col ${getCalDayCellClass(isSelected, isToday, hasEvents)}`}
                        data-testid={`cal-day-${cell.dateKey}`}
                      >
                        <span className={`text-xs font-medium ${isToday ? 'text-primary font-bold' : 'text-foreground'}`}>
                          {cell.day}
                        </span>
                        {hasEvents && (
                          <div className="flex flex-wrap gap-0.5 mt-1">
                            {cell.events.slice(0, 3).map((ev, i) => (
                                <span
                                  key={ev.id || `ev-${i}`}
                                  className={`h-1.5 w-1.5 rounded-full ${getEventDotColor(ev, today)}`}
                                />
                              ))}
                            {cell.events.length > 3 && (
                              <span className="text-[10px] text-muted-foreground ml-0.5">+{cell.events.length - 3}</span>
                            )}
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
                {/* Legend */}
                <div className="flex items-center gap-4 mt-4 pt-3 border-t">
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <span className="h-2 w-2 rounded-full bg-red-500" /> Overdue
                  </div>
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <span className="h-2 w-2 rounded-full bg-blue-500" /> Upcoming
                  </div>
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <span className="h-2 w-2 rounded-full bg-green-500" /> Filed
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Selected Date Panel */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium">
                  {selectedDate
                    ? formatDate(selectedDate)
                    : 'Select a date'}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ScrollArea className="max-h-[500px]">
                  {!selectedDate && (
                    <p className="text-sm text-muted-foreground text-center py-8">Click on a date to see compliance items</p>
                  )}
                  {selectedDate && selectedEvents.length === 0 && (
                    <p className="text-sm text-muted-foreground text-center py-8">No compliance items on this date</p>
                  )}
                  {selectedDate && selectedEvents.length > 0 && (
                    <div className="space-y-3">
                      {selectedEvents.map(ev => (
                        <div key={ev.id} className="p-3 rounded-lg border hover:bg-muted/50 transition-colors duration-150">
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-sm truncate">{ev.title || ev.task_title}</p>
                              {ev.client_name && (
                                <p className="text-xs text-muted-foreground mt-0.5">{ev.client_name}</p>
                              )}
                              {ev.board_name && (
                                <p className="text-xs text-muted-foreground">{ev.board_name}</p>
                              )}
                            </div>
                            <Badge variant="secondary" className={`text-xs shrink-0 ${STAGE_COLORS[ev.stage] || ''}`}>
                              {STAGE_LABELS[ev.stage] || ev.stage}
                            </Badge>
                          </div>
                          {canManage && ev.itemType === 'task' && ev.stage !== 'filed' && (
                            <Button
                              variant="outline"
                              size="sm"
                              className="mt-2 w-full text-xs"
                              onClick={() => handleSendReminder(ev.id)}
                              disabled={sendingReminder === ev.id}
                              data-testid={`send-reminder-${ev.id}`}
                            >
                              <Send className="h-3 w-3 mr-1" />
                              {sendingReminder === ev.id ? 'Sending...' : 'Send Reminder'}
                            </Button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </ScrollArea>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* List View */}
        <TabsContent value="list">
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">Compliance Reminders</CardTitle>
                <Select value={filterUrgency} onValueChange={setFilterUrgency}>
                  <SelectTrigger className="w-[160px]" data-testid="urgency-filter">
                    <SelectValue placeholder="Filter" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="overdue">Overdue</SelectItem>
                    <SelectItem value="due_today">Due Today</SelectItem>
                    <SelectItem value="due_this_week">Due This Week</SelectItem>
                    <SelectItem value="upcoming">Upcoming</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            <CardContent>
              {filteredReminders.length === 0 ? (
                <div className="text-center py-12">
                  <CheckCircle2 className="h-12 w-12 text-green-500 mx-auto mb-3" />
                  <p className="font-medium">All clear!</p>
                  <p className="text-sm text-muted-foreground mt-1">No compliance items match this filter</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {filteredReminders.map(r => {
                    const cfg = URGENCY_CONFIG[r.status] || URGENCY_CONFIG.upcoming;
                    const UrgencyIcon = cfg.icon;
                    return (
                      <div
                        key={r.id}
                        className={`p-4 rounded-lg border ${cfg.color} transition-shadow duration-200 hover:shadow-[var(--shadow-sm)]`}
                        data-testid={`reminder-item-${r.id}`}
                      >
                        <div className="flex items-start gap-3">
                          <UrgencyIcon className="h-5 w-5 mt-0.5 shrink-0" />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-2">
                              <div>
                                <p className="font-medium text-sm">{r.title}</p>
                                {r.client_name && (
                                  <p className="text-xs opacity-80 mt-0.5">{r.client_name}</p>
                                )}
                              </div>
                              <div className="flex items-center gap-2 shrink-0">
                                <Badge variant="secondary" className={`text-xs ${STAGE_COLORS[r.stage] || ''}`}>
                                  {STAGE_LABELS[r.stage] || r.stage}
                                </Badge>
                              </div>
                            </div>
                            <div className="flex items-center justify-between mt-2">
                              <span className="text-xs opacity-70">
                                Due: {formatDate(r.compliance_due_date)}
                              </span>
                              {canManage && r.needs_action && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="h-7 text-xs"
                                  onClick={() => handleSendReminder(r.id)}
                                  disabled={sendingReminder === r.id}
                                  data-testid={`list-send-reminder-${r.id}`}
                                >
                                  <Bell className="h-3 w-3 mr-1" />
                                  {sendingReminder === r.id ? 'Sending...' : 'Send Reminder'}
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ComplianceCalendarPage;
